import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { Modal, ModalTitle } from "@/components/Modal";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatDateTime";
import { parseRecording, type Recording } from "@/lib/trdp/recording";
import { RdpPlayer } from "./RdpPlayer";
import { RecordingsPage } from "./RecordingsPage";
import type { SessionRecordingSummary } from "./shared";

function formatResolution(recording: SessionRecordingSummary): string {
  if (!recording.width || !recording.height) return "Unknown";

  return `${recording.width} × ${recording.height}`;
}

export function RdpRecordings() {
  const { orgId } = useParams();

  return (
    <RecordingsPage
      connectionMethod="RDP"
      label="RDP"
      extraColumn={{ label: "Resolution", render: formatResolution }}
      renderModal={(recording, onClose) =>
        orgId && (
          <ReplayModal orgId={orgId} recording={recording} onClose={onClose} />
        )
      }
    />
  );
}

function ReplayModal({
  orgId,
  recording,
  onClose,
}: {
  orgId: string;
  recording: SessionRecordingSummary;
  onClose: () => void;
}) {
  const api = useApi();
  const [parsed, setParsed] = useState<Recording | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRecording() {
      const content = await api.getBinary(
        `/organizations/${orgId}/session-recordings/${recording.id}/content`,
      );

      if (cancelled) return;

      if (!content) {
        setError("Couldn't load the recording.");

        return;
      }

      try {
        const decoded = await parseRecording(content);

        if (!cancelled) setParsed(decoded);
      } catch (cause: unknown) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      }
    }

    void loadRecording();

    return () => {
      cancelled = true;
    };
  }, [api, orgId, recording.id]);

  return (
    <Modal onClose={onClose} maxWidth="max-w-5xl" closeOnBackdropClick={false}>
      <div className="mb-4 shrink-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <ModalTitle>{recording.resourceName}</ModalTitle>
          {recording.truncated && (
            <Chip variant="warning">Incomplete recording</Chip>
          )}
        </div>

        <p className="text-sm text-foreground/70">
          {recording.requestedByUserName ??
            recording.requestedByUserEmail ??
            "Unknown"}{" "}
          · {formatDateTime(recording.requestedAt)}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <RdpPlayer recording={parsed} error={error} />
      </div>

      <div className="mt-4 flex shrink-0 justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}
